import React from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { Status, statusShape } from '../consts';

type ActivityStatusProps = StylableComponent<HTMLDivElement> & {
  status: Status;
};

/**
 * ActivityStatus component. Describes a single status, containing text & color.
 */
const ActivityStatus = React.forwardRef<HTMLDivElement, ActivityStatusProps>(
  ({ status, style, className, ...props }, ref) => {
    const { text, color, subtext } = status;
    const statusTokens = useTheme().tokens?.component?.activityLog?.status;
    const labelTokens = statusTokens?.label;
    const subtextTokens = statusTokens?.subtext;

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            alignItems: 'center',
          },
          style,
        ]}
        {...props}
      >
        <div
          css={{
            minWidth: 3,
            height: subtext ? 32 : 20,
            borderRadius: statusTokens?.['dot-border-radius'],
            backgroundColor: color,
          }}
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: statusTokens?.marginLeft,
          }}
        >
          <div dir="auto" css={labelTokens}>
            {text}
          </div>
          {subtext && (
            <div dir="auto" css={subtextTokens}>
              {subtext}
            </div>
          )}
        </div>
      </div>
    );
  },
);

ActivityStatus.displayName = 'ActivityStatus';

ActivityStatus.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The status */
  status: statusShape.isRequired,
};

export default ActivityStatus;
